import React, { FunctionComponent, useEffect, useState } from 'react'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import { useTranslation } from 'react-i18next'

interface PublicationProgressBarProps {
  progressBarRef: React.RefObject<HTMLDivElement | null>
  publicationAreaRef: React.RefObject<HTMLDivElement | null>
  documentHeight: number | 0
  tocContainerRef: React.RefObject<HTMLDivElement | null>
}

interface SectionI {
  href: string
  position: number
  htmlElement: HTMLElement
  sectionHeading: string
  sectionId: string
  sectionText: string
}

interface ProgressBarSectionAnchorProps {
  progressPercentage: number
  section: SectionI
  documentHeight: number | 0
}

const ProgressBarSectionAnchor = ({ section, progressPercentage, documentHeight }: ProgressBarSectionAnchorProps) => {
  const [position, setPosition] = React.useState(0)

  useEffect(() => {
    const targetId = section.href.split('#')[1]
    const targetElement = document.getElementById(targetId)
    const pos = targetElement ? ((targetElement.offsetTop - 50) / documentHeight) * 100 : 0
    setPosition(pos)
  }, [section.href, documentHeight])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    const targetId = section.href.split('#')[1]
    const targetElement = document.getElementById(targetId)

    if (targetElement) {
      const headerOffset = 50
      const scrollPosition = targetElement.offsetTop - headerOffset

      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <>
      <button
        className={
          progressPercentage >= position
            ? 'progress-bar__indicator-anchor progress-bar__indicator-anchor--complete'
            : 'progress-bar__indicator-anchor'
        }
        style={{ left: `${position}%` }}
        data-tooltip-id={section.sectionId}
        data-tooltip-place="top"
        data-tooltip-content={section.sectionHeading}
        onClick={handleClick}
        aria-label={'Add aria-label for en and sv'} // TODO: Add aria-label text
      ></button>
      <ReactTooltip id={section.sectionId} className="tooltip" style={{ zIndex: 999 }} />
    </>
  )
}

const RecursiveProgressBarSection = ({
  section,
  progressPercentage,
  documentHeight,
}: {
  section: SectionI
  progressPercentage: number
  documentHeight: number
}) => (
  <>
    <ProgressBarSectionAnchor
      section={section}
      progressPercentage={progressPercentage}
      documentHeight={documentHeight}
      key={section.sectionId}
    />
  </>
)

const PublicationProgressBar: FunctionComponent<PublicationProgressBarProps> = (props) => {
  const { tocContainerRef, progressBarRef, documentHeight } = props
  const [progressBarLinks, setProgressBarLinks] = useState<SectionI[]>([])
  const { t } = useTranslation()

  const createOrUpdateProgressBarLinks = function () {
    const tocContainer = tocContainerRef.current
    const progressBar = progressBarRef.current
    if (progressBar && tocContainer) {
      var progressBarDivs = Array.from(progressBar.querySelectorAll('button'))

      if (progressBarDivs.length > 0) {
        const lastProgressPoint = progressBarDivs[progressBarDivs.length - 1]
        const left = window.getComputedStyle(lastProgressPoint).left
        if (left !== '-3px') {
          return
        }
      }
      setProgressBarLinks([])

      const anchorLinks: NodeListOf<HTMLAnchorElement> = tocContainer.querySelectorAll('.toc1 > li a[href^="#"]')

      anchorLinks.forEach((link, index) => {
        const href = link.href
        const targetSwtring = href.split('#')[1]
        const target = document.getElementById(targetSwtring) as HTMLElement

        if (target) {
          const h2target = target.tagName === 'H2' ? target : target.closest('h2')
          if (h2target) {
            const targetScroll = h2target?.getBoundingClientRect().top + window.scrollY
            const height = document.documentElement.scrollHeight - window.innerHeight
            const targetpos = (targetScroll / height) * 100

            const newLink: SectionI = {
              href: href,
              position: targetpos,
              sectionText: h2target.innerText,
              htmlElement: h2target,
              sectionHeading: h2target.innerText,
              sectionId: index.toString(),
            }

            setProgressBarLinks((prevLinks) => [...prevLinks, newLink])
          }
        }
      })
    }
  }

  const [progressPercentage, setProgressPercentage] = useState(0)
  let ticking = false // Prevents multiple rAF calls in a single frame

  useEffect(() => {
    const updateProgress = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      const percentage = (scrollY / documentHeight) * 100

      setProgressPercentage(scrollY + window.innerHeight >= documentHeight ? 100 : percentage)
      ticking = false
    }

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateProgress)
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [documentHeight])

  useEffect(() => {
    if (documentHeight > 0) {
      createOrUpdateProgressBarLinks()
    }
  }, [documentHeight])

  return (
    <div className="progress-wrapper">
      <div className="progress-container">
        <nav
          id="progressBar"
          aria-label={t('progress-indicator')}
          className="progress-bar"
          ref={progressBarRef}
          style={{ width: `${progressPercentage}%` }}
          key={progressBarLinks.length} // Force re-render when links update
        >
          {progressBarLinks.map((s, index) => (
            <RecursiveProgressBarSection
              section={s}
              progressPercentage={progressPercentage}
              documentHeight={documentHeight}
              key={index}
            />
          ))}
        </nav>
      </div>
    </div>
  )
}

export default PublicationProgressBar
